import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/axios';
import compressImage from '../utils/imageCompression';
import AppShell from '../components/AppShell';
import BodyItemRow from '../components/BodyItemRow';
import BodyDamageDiagram from '../components/BodyDamageDiagram';
import BodyPartStatusModal from '../components/BodyPartStatusModal';
import SaveModal from '../components/SaveModal';
import {
  DEFAULT_BODY_RESULT_OPTIONS,
  getResultLookupType,
  normalizeResultOptions,
  mostSevereStatus,
  normalizeStatusList,
  readStatusList,
} from '../utils/resultOptions';

function normalizeZoneKeyForStyle(zoneKey, bodyStyle) {
  if (!zoneKey) return zoneKey;

  if (bodyStyle === 'trailer' && ['trunk', 'rear_hatch', 'tailgate'].includes(zoneKey)) return 'rear_chassis';
  if (bodyStyle === 'box_truck' && ['trunk', 'rear_hatch', 'tailgate'].includes(zoneKey)) return 'rear_box';
  if (bodyStyle === 'chassis_cab' && ['trunk', 'rear_hatch', 'tailgate'].includes(zoneKey)) return 'rear_chassis';
  if (bodyStyle === 'hatchback' && zoneKey === 'trunk') return 'rear_hatch';
  if ((bodyStyle === 'pickup' || bodyStyle === 'van') && zoneKey === 'trunk') return 'tailgate';
  if (bodyStyle === 'sedan' && (zoneKey === 'rear_hatch' || zoneKey === 'tailgate')) return 'trunk';
  if (bodyStyle === 'suv' && zoneKey === 'tailgate') return 'trunk';

  return zoneKey;
}

function deriveConditionStatus(result) {
  if (!result) return null;
  if (result.condition_status) return result.condition_status;
  if (result.is_riveted) return 'riveted';
  if (result.is_replaced || result.is_dented) return 'defect';
  if (result.is_modified || result.is_sprayed || result.is_loose || result.is_plastic || result.is_protected) {
    return 'damage';
  }
  // سطر بلا أي حالة = بلا نتيجة، لا «جيد».
  return null;
}

function legacyFlagsForStatuses(statuses) {
  const list = normalizeStatusList(statuses);
  const base = {
    is_sprayed: false,
    is_modified: false,
    is_replaced: false,
    is_riveted: false,
    is_dented: false,
    is_plastic: false,
    is_protected: false,
    is_loose: false,
  };

  // كل حالة مختارة ترفع علمها المقابل، فتبقى الأعلام القديمة متوافقة
  // مع الاختيار المتعدد بدل أن تعكس حالة واحدة فقط.
  list.forEach((status) => {
    if (status === 'damage') base.is_modified = true;
    if (status === 'defect') base.is_dented = true;
    if (status === 'riveted') base.is_riveted = true;
    if (status === 'sprayed') base.is_sprayed = true;
    if (status === 'replaced') base.is_replaced = true;
  });

  return base;
}

const COUPE_EXCLUDED_ZONE_KEYS = new Set(['left_rear_door', 'right_rear_door']);

// الشاسي كاب: كابينة ببابين وشاسيه عارٍ، فلا أبواب خلفية ولا جنب ولا رفرف خلفي.
// شاحنة الصندوق: كابينة ببابين وصندوق خلفي، فلا أبواب خلفية ولا جنب ولا رفرف خلفي.
const BOX_TRUCK_EXCLUDED_ZONE_KEYS = new Set([
  'left_rear_door', 'right_rear_door',
  'left_rear_fender', 'right_rear_fender',
  'left_side_panel', 'right_side_panel',
]);

const CHASSIS_EXCLUDED_ZONE_KEYS = new Set([
  'left_rear_door', 'right_rear_door',
  'left_rear_fender', 'right_rear_fender',
  'left_side_panel', 'right_side_panel',
]);

function mergeItemValue(values, itemId, payload) {
  const hasStatusChange = payload.condition_statuses !== undefined || payload.condition_status !== undefined;

  let mergedPayload = payload;
  if (hasStatusChange) {
    const statuses = normalizeStatusList(
      payload.condition_statuses !== undefined
        ? payload.condition_statuses
        : [payload.condition_status],
    );

    mergedPayload = {
      ...legacyFlagsForStatuses(statuses),
      ...payload,
      condition_statuses: statuses,
      condition_status: mostSevereStatus(statuses),
    };
  }

  return { ...values, [itemId]: { ...values[itemId], ...mergedPayload, item_id: itemId } };
}

export default function BodyInspection() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [values, setValues] = useState({});
  const [inspectionState, setInspectionState] = useState(null);
  const [resultOptions, setResultOptions] = useState(DEFAULT_BODY_RESULT_OPTIONS);
  const [showModal, setShowModal] = useState(false);
  const [selectedPartItem, setSelectedPartItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [partSaving, setPartSaving] = useState(false);
  const [savedNotice, setSavedNotice] = useState('');
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');

  const errorText = (err) => {
    if (err?.userMessage) return err.userMessage;
    if (err?.code === 'ECONNABORTED' || String(err?.message || '').includes('timeout')) {
      return 'الخادم استغرق وقتًا أطول من المتوقع. حاول مرة أخرى.';
    }
    return err?.response?.data?.message || 'حدث خطأ أثناء تحميل بيانات فحص البودي.';
  };

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      setLoading(true);
      setError('');
      try {
        const [itemsRes, inspectionRes, lookupRes] = await Promise.all([
          api.get('/inspection-items/by-section/body'),
          api.get(`/inspections/${id}`),
          api.get(`/lookups/by-type/${getResultLookupType('body')}`).catch(() => ({ data: [] })),
        ]);

        if (!mounted) return;
        setItems(itemsRes.data);
        setInspectionState(inspectionRes.data);
        setResultOptions(normalizeResultOptions(lookupRes.data, 'body'));

        const currentBodyResults = inspectionRes.data?.body_results || [];
        const mapped = {};
        currentBodyResults.forEach((result) => {
          if (!result?.inspection_item_id) return;
          const savedStatuses = readStatusList(result);
          mapped[result.inspection_item_id] = {
            item_id: result.inspection_item_id,
            condition_statuses: savedStatuses,
            condition_status: mostSevereStatus(savedStatuses) || deriveConditionStatus(result) || null,
            is_sprayed: Boolean(result.is_sprayed),
            is_modified: Boolean(result.is_modified),
            is_replaced: Boolean(result.is_replaced),
            is_riveted: Boolean(result.is_riveted),
            is_dented: Boolean(result.is_dented),
            is_plastic: Boolean(result.is_plastic),
            is_protected: Boolean(result.is_protected),
            is_loose: Boolean(result.is_loose),
            notes: result.notes || '',
          };
        });
        (inspectionRes.data?.attachments || []).forEach((attachment) => {
          if (attachment?.section !== 'body' || !attachment?.inspection_item_id) return;
          mapped[attachment.inspection_item_id] = {
            ...(mapped[attachment.inspection_item_id] || {}),
            item_id: attachment.inspection_item_id,
            attachment,
          };
        });
        setValues(mapped);
      } catch (err) {
        if (!mounted) return;
        setError(errorText(err));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (!savedNotice) return undefined;
    const timer = setTimeout(() => setSavedNotice(''), 2500);
    return () => clearTimeout(timer);
  }, [savedNotice]);

  // تحذير المتصفح عند مغادرة الصفحة وفيها تعديلات لم تُحفظ.
  useEffect(() => {
    if (!dirty) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [dirty]);

  const setItemValue = (itemId, payload) => {
    setValues((prev) => mergeItemValue(prev, itemId, payload));
    setDirty(true);
  };

  const upload = async (itemId, rawFile) => {
    if (!rawFile) return;
    const file = await compressImage(rawFile);
    const form = new FormData();
    const currentValue = values[itemId] || {};
    form.append('file', file);
    form.append('section', 'body');
    form.append('item_id', itemId);
    form.append('condition_status', currentValue.condition_status || '');
    // نرسل كل الحالات المختارة حتى لا يختزلها الخادم إلى الأشد عند رفع الصورة.
    form.append('condition_statuses', (currentValue.condition_statuses || []).join(','));
    form.append('is_sprayed', currentValue.is_sprayed ? '1' : '0');
    form.append('is_modified', currentValue.is_modified ? '1' : '0');
    form.append('is_replaced', currentValue.is_replaced ? '1' : '0');
    form.append('is_riveted', currentValue.is_riveted ? '1' : '0');
    form.append('is_dented', currentValue.is_dented ? '1' : '0');
    form.append('is_plastic', currentValue.is_plastic ? '1' : '0');
    form.append('is_protected', currentValue.is_protected ? '1' : '0');
    form.append('is_loose', currentValue.is_loose ? '1' : '0');
    form.append('notes', currentValue.notes || '');
    try {
      const response = await api.post(`/inspections/${id}/attachments`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      setItemValue(itemId, { attachment: response.data });
    } catch (err) {
      setError(errorText(err));
    }
  };

  const removeImage = async (itemId, attachment) => {
    if (!attachment?.id) {
      setItemValue(itemId, { attachment: null });
      return;
    }

    try {
      await api.delete(`/inspections/${id}/attachments/${attachment.id}`);
      setItemValue(itemId, { attachment: null });
    } catch (err) {
      setError(errorText(err));
    }
  };

  const activeBodyStyle = inspectionState?.diagram_body_style || 'sedan';
  const filteredItems = useMemo(() => {
    const strictMatches = items.filter((item) => {
      const supports = item?.supported_body_styles;
      if (!Array.isArray(supports) || supports.length === 0) return true;
      return supports.includes(activeBodyStyle);
    });

    // Fallback: some installations were seeded without `coupe` in supported_body_styles.
    // If strict filtering returns nothing, use all body items so diagram editing remains usable.
    const styleMatches = strictMatches.length > 0 ? strictMatches : items;

    if (activeBodyStyle === 'coupe') {
      return styleMatches.filter((item) => !COUPE_EXCLUDED_ZONE_KEYS.has(item?.diagram_zone_key));
    }

    if (activeBodyStyle === 'chassis_cab') {
      return styleMatches.filter((item) => !CHASSIS_EXCLUDED_ZONE_KEYS.has(item?.diagram_zone_key));
    }

    if (activeBodyStyle === 'box_truck') {
      return styleMatches.filter((item) => !BOX_TRUCK_EXCLUDED_ZONE_KEYS.has(item?.diagram_zone_key));
    }

    // التريلا قاطرة بشاسيه خلفي، فتستبعد ما يستبعده الشاسي كاب.
    if (activeBodyStyle === 'trailer') {
      return styleMatches.filter((item) => !CHASSIS_EXCLUDED_ZONE_KEYS.has(item?.diagram_zone_key));
    }

    return styleMatches;
  }, [items, activeBodyStyle]);

  const payloadItems = useMemo(() => {
    const allowedIds = new Set(filteredItems.map((item) => item.id));
    return Object.values(values).filter((entry) => allowedIds.has(entry?.item_id));
  }, [values, filteredItems]);

  const selectedZoneKey = selectedPartItem
    ? normalizeZoneKeyForStyle(selectedPartItem.diagram_zone_key, activeBodyStyle)
    : null;

  const openPartEditor = (zoneKey) => {
    const matchedItem = filteredItems.find(
      (item) => normalizeZoneKeyForStyle(item.diagram_zone_key, activeBodyStyle) === zoneKey,
    );

    if (!matchedItem) {
      setError('لا يوجد عنصر فحص مهيأ لهذا الجزء في النظام حتى الآن. يمكنك إضافته من إعدادات عناصر الفحص.');
      return;
    }

    setError('');
    setSelectedPartItem(matchedItem);
  };

  // مصدر واحد لإرسال نتائج البودي: زر الحفظ السفلي، ونافذة تعديل الجزء.
  const persistBody = async (entries, { markDone }) => {
    const allowedIds = new Set(filteredItems.map((item) => item.id));
    const items = entries.filter((entry) => allowedIds.has(entry?.item_id));

    await api.post(`/inspections/${id}/body`, { items, mark_done: markDone });
    setDirty(false);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await persistBody(payloadItems, { markDone: true });
      setInspectionState((prev) => ({ ...(prev || {}), body_done: true }));
      setShowModal(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  // زر "حفظ التغييرات" داخل نافذة الجزء كان يحدّث الحالة محليًا فقط،
  // فتضيع النتيجة إن غادر المستخدم الشاشة دون الضغط على الحفظ السفلي.
  const savePart = async (itemId, payload) => {
    const nextValues = mergeItemValue(values, itemId, payload);
    setValues(nextValues);
    setSelectedPartItem(null);
    setPartSaving(true);
    setError('');

    try {
      await persistBody(Object.values(nextValues), { markDone: false });
      setSavedNotice('تم حفظ حالة الجزء.');
    } catch (err) {
      setDirty(true);
      setError(errorText(err));
    } finally {
      setPartSaving(false);
    }
  };

  return (
    <AppShell
      title="فحص البودي"
      actions={<button className="px-3 py-2 rounded-lg border" onClick={() => navigate(`/inspection/${id}/menu`)}>رجوع للقائمة</button>}
    >
      {loading && (
        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-3 text-blue-700 text-sm">
          جاري تحميل بيانات فحص البودي...
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      {partSaving && (
        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-3 text-blue-700 text-sm">
          جاري حفظ حالة الجزء...
        </div>
      )}

      {!partSaving && savedNotice && (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-emerald-700 text-sm">
          {savedNotice}
        </div>
      )}

      {dirty && !partSaving && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-800 text-sm">
          لديك تعديلات لم تُحفظ بعد. اضغط زر «حفظ» في أسفل الصفحة حتى تظهر في التقرير.
        </div>
      )}

      <BodyDamageDiagram
        bodyStyle={activeBodyStyle}
        items={filteredItems}
        values={values}
        title="خريطة أضرار البودي"
        activeZoneKey={selectedZoneKey}
        onPartSelect={openPartEditor}
        resultOptions={resultOptions}
      />

      <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
        اضغط على أي جزء من السيارة لتحديد حالته وإضافة ملاحظة. التلوين: أخضر = جيد، برتقالي = خدش، أصفر = ضرر، أحمر = تلف، أزرق = مسمكر.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filteredItems.map((item) => (
          <BodyItemRow key={item.id} item={item} value={values[item.id]} onChange={setItemValue} onUpload={upload} onRemoveImage={removeImage} resultOptions={resultOptions} />
        ))}
      </div>

      <div className="mt-6">
        <button
          onClick={save}
          disabled={saving || loading}
          className="rounded-xl bg-primary px-6 py-3 text-white font-bold disabled:opacity-50"
        >
          {saving ? 'جاري الحفظ...' : dirty ? 'حفظ التعديلات غير المحفوظة' : 'حفظ'}
        </button>
      </div>

      <SaveModal open={showModal} onClose={() => setShowModal(false)} inspectionId={id} state={inspectionState} />

      <BodyPartStatusModal
        open={Boolean(selectedPartItem)}
        item={selectedPartItem}
        value={selectedPartItem ? values[selectedPartItem.id] : null}
        resultOptions={resultOptions}
        onClose={() => setSelectedPartItem(null)}
        onSave={(payload) => {
          if (!selectedPartItem) return;
          savePart(selectedPartItem.id, payload);
        }}
      />
    </AppShell>
  );
}
